// React and ReactDOM imports
import React from 'react';
import { createRoot } from 'react-dom/client';

// Local fonts and icons (avoid CDN / CSP issues)
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import 'bootstrap-icons/font/bootstrap-icons.min.css';

// i18n initialization (must be before any component rendering)
import './i18n';

// Redux Provider import
import { Provider, useSelector } from 'react-redux';

// Redux Persist import
import { PersistGate } from 'redux-persist/integration/react';

// React Router import
import { BrowserRouter } from 'react-router-dom';

// TanStack Query import
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from 'Configuration/QueryClientConfiguration';

// MSAL imports
import { MsalProvider } from '@azure/msal-react';
import { msalInstance, initializeMsal, loginRequest } from 'Configuration/MsalConfiguration';
import { getMsalIdToken } from 'Services/MsalTokenService';
import { jwtDecode } from 'jwt-decode';

// dayjs import
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

// App and Unauthorized views imports
import App from 'App';
import Unauthorized from 'Views/Unauthorized';
import Maintenance from 'Views/Maintenance';

// Store configuration import
import { store, persistor } from 'Configuration/StoreConfiguration';

// Services import
import RestClient from 'Services/RestClientService';
import logger from 'Services/MyTuiProfileCustomLogger';

// Utils import
import { normalizeStaffResponse } from 'Utils/ApiResponseHelper';

// Admin emails configuration
const ADMIN_EMAILS = ['joseenrique.mateu@tui.com', 'rebecca.powell@tui.co.uk'];

// Function to check if current time is within maintenance window
const isInMaintenanceWindow = (tppSettings) => {
  if (!tppSettings?.maintenanceStart || !tppSettings?.maintenanceEnd) {
    return false;
  }
  const now = dayjs.utc();
  const start = dayjs.utc(tppSettings.maintenanceStart);
  const end = dayjs.utc(tppSettings.maintenanceEnd);
  return (now.isAfter(start) && now.isBefore(end)) || now.isSame(start) || now.isSame(end);
};

// MaintenanceWrapper component to check maintenance status from Redux
const MaintenanceWrapper = ({ isAdmin, hasValidRoles, decodedToken, rawToken }) => {
  const tppSettings = useSelector((state) => state.settings);
  const maintenanceActive = isInMaintenanceWindow(tppSettings);

  // If user doesn't have valid roles, show unauthorized
  if (!hasValidRoles) {
    return (
      <MsalProvider instance={msalInstance}>
        <Unauthorized errorMessage="401" decodedToken={decodedToken} rawToken={rawToken} />
      </MsalProvider>
    );
  }

  // If maintenance is active and user is not admin, show maintenance page
  if (maintenanceActive && !isAdmin) {
    return (
      <MsalProvider instance={msalInstance}>
        <Maintenance startDate={tppSettings.maintenanceStart} endDate={tppSettings.maintenanceEnd} />
      </MsalProvider>
    );
  }

  // Show the app
  return (
    <MsalProvider instance={msalInstance}>
      <App
        isAdmin={isAdmin}
        maintenanceActive={maintenanceActive}
        maintenanceStart={tppSettings?.maintenanceStart}
        maintenanceEnd={tppSettings?.maintenanceEnd}
      />
    </MsalProvider>
  );
};

// Create root instance
const rootElement = document.getElementById('root');
const root = createRoot(rootElement);

// Function to render the application
const renderApp = (Component) => {
  root.render(
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>{Component}</BrowserRouter>
        </QueryClientProvider>
      </PersistGate>
    </Provider>
  );
};

// Initialize and run the application with MSAL authentication
const initializeApp = async () => {
  let profile = null;
  let rawToken = null;
  try {
    // Initialize MSAL
    await initializeMsal();

    // Handle redirect promise
    await msalInstance.handleRedirectPromise();

    // Check if user is authenticated
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      // No accounts, redirect to login
      await msalInstance.loginRedirect(loginRequest);
      return;
    }

    // Acquire token silently and decode to get profile
    const idToken = await getMsalIdToken();
    rawToken = idToken;
    profile = jwtDecode(idToken);

    // Check if user is admin
    const userEmail = (profile.email || '').toLowerCase();
    const isAdmin = ADMIN_EMAILS.map((e) => e.toLowerCase()).includes(userEmail);

    // Validate roles
    const roles = profile.roles || [];
    logger.debug('User roles:', { roles });
    const validRoles = roles.filter((value) => value.includes('DS_MTP') || value.includes('DS_GPX'));
    logger.debug('Valid roles found:', { validRoles });
    const hasValidRoles = validRoles.length > 0;

    // If no valid roles, log the full decoded token for debugging with AD team
    if (!hasValidRoles) {
      logger.warn('ACCESS DENIED - No valid roles found. Full decoded AD token:', {
        fullToken: profile,
        allClaims: Object.keys(profile),
        roles: profile.roles,
        groups: profile.groups,
        wids: profile.wids,
        email: profile.email,
        name: profile.name,
        aud: profile.aud,
        iss: profile.iss,
        tid: profile.tid,
      });
      console.warn('[MTP] ACCESS DENIED - Full decoded AD token for debugging:', JSON.stringify(profile, null, 2));
    }

    // Validate email format
    const email = profile.email;
    const isUpnEmail = email ? email.includes('@') : false;
    if (!isUpnEmail) {
      throw new Error('400');
    }

    // Get staff ID from TPP
    const params = new URLSearchParams({ tuiEmail: email });
    logger.debug('Fetching staff data for email:', { email });

    const staffResult = await RestClient.Get(`staff?${params}`);
    logger.debug('Staff result:', { staffResult });

    // Normalize staff response
    const staffArray = normalizeStaffResponse(staffResult);
    const staffData = staffArray[0];
    logger.debug('Staff data:', { staffData });

    const staffId = staffData ? staffData.staffID : null;
    logger.debug('Staff ID:', { staffId });

    if (!staffId) {
      logger.error('No staff ID found for email:', { email });
      throw new Error('404');
    }

    // Render the main application with MaintenanceWrapper
    renderApp(
      <MaintenanceWrapper isAdmin={isAdmin} hasValidRoles={hasValidRoles} decodedToken={profile} rawToken={rawToken} />
    );
  } catch (error) {
    // If auth session is dead, redirect to login instead of showing error screen
    if (error.message?.includes('No account found') || error.message?.includes('User must log in')) {
      logger.warn('Session expired during startup, redirecting to login', { component: 'index' });
      await msalInstance.loginRedirect(loginRequest);
      return;
    }

    logger.error('Startup Error', error, { component: 'index' });
    // Render the Unauthorized view with the error message
    renderApp(
      <MsalProvider instance={msalInstance}>
        <Unauthorized errorMessage={error.message} decodedToken={profile} rawToken={rawToken} />
      </MsalProvider>
    );
  }
};

// Register Service Worker for offline support and caching
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

// Start the application
initializeApp();

// Register the service worker after app initialization
serviceWorkerRegistration.register({
  onUpdate: (_registration) => {
    // A new version is available — the next reload will use the updated SW
    logger.debug('New content is available; please refresh.', { component: 'serviceWorker' });
  },
});
